/**
 * Brand registry — keyed by `import.meta.env.VITE_BRAND` build-time env var.
 *
 * One frontend codebase, four brand faces:
 *   - `valko`     — consumer voice AI (purple, voiceOnlyMode)
 *   - `protofac`  — developer agentic tools (amber)
 *   - `siynergia` — enterprise team orchestration (deep blue) — spelled I-Y, NOT Y-I
 *   - `brato`     — legacy dogfood face used at voice.akrsmv.net (default)
 *
 * Default is `brato` so the existing voice.akrsmv.net deployment is unaffected
 * until Phase 5 of `server-config/plans/three-product-deployment.md` flips it
 * to `protofac`.
 *
 * Build-time selection happens via `VITE_BRAND` (Vite-required prefix). The
 * Dockerfile accepts `PUBLIC_BRAND` build arg → translated to `VITE_BRAND` env
 * for the build step.
 *
 * `voiceOnlyMode: true` is a UI-side defense-in-depth gate: even if backend
 * feature flags somehow report `commands_enabled` / `ssh_terminal_enabled` /
 * etc. as true, the frontend MUST NOT render terminal / git / PR / file-tree
 * surfaces. Backend gates remain authoritative for security; this gate is
 * purely about preventing a misconfiguration from leaking developer UI to
 * Valko consumers.
 */

export type BrandId = 'valko' | 'protofac' | 'siynergia' | 'brato';

export interface Brand {
  /** Display name shown in headers, page title, install prompts */
  name: string;
  /** Short marketing-ish line for auth/install screens */
  tagline: string;
  /** Primary accent color — surfaced as `--brand-accent` CSS custom property */
  accentColor: string;
  /** Logo asset path (served from `/public/brand-logos/*.svg`) */
  logoSrc: string;
  /** Document title set on boot */
  pageTitle: string;
  /**
   * UI-side voice-only safety flag. When `true`, the frontend hides all
   * coding/terminal/git/PR/file-tree UI regardless of backend feature flags.
   * Defense in depth — backend gates still apply, this is belt-and-braces.
   */
  voiceOnlyMode: boolean;
}

export const brands: Record<BrandId, Brand> = {
  valko: {
    name: 'Valko',
    tagline: 'Your voice companion.',
    accentColor: '#7c3aed',
    logoSrc: '/brand-logos/valko.svg',
    pageTitle: 'Valko',
    voiceOnlyMode: true,
  },
  protofac: {
    name: 'Protofac',
    tagline: 'Agentic prototyping.',
    accentColor: '#d97706',
    logoSrc: '/brand-logos/protofac.svg',
    pageTitle: 'Protofac',
    voiceOnlyMode: false,
  },
  siynergia: {
    name: 'Siynergia',
    tagline: 'Team orchestration at scale.',
    accentColor: '#1d4ed8',
    logoSrc: '/brand-logos/siynergia.svg',
    pageTitle: 'Siynergia',
    voiceOnlyMode: false,
  },
  // Legacy dogfood brand. Kept visually identical to today's voice.akrsmv.net
  // until Phase 5 rebrands it to Protofac. Accent matches the `--accent-green`
  // theme used by the cartoon-letter wordmark, so existing styles are unchanged.
  brato: {
    name: 'Brato',
    tagline: 'Control Claude Code via voice.',
    accentColor: '#4ec9b0',
    logoSrc: '/brand-logos/brato.svg',
    pageTitle: 'Brato',
    voiceOnlyMode: false,
  },
};

/**
 * Resolve the active brand for this build. Falls back to `brato` when
 * `VITE_BRAND` is unset OR set to an unknown value — preserves current
 * voice.akrsmv.net behaviour.
 */
export function getBrand(): Brand {
  const raw = (import.meta.env.VITE_BRAND as string | undefined) ?? '';
  const id = raw.trim().toLowerCase() as BrandId;
  return brands[id] ?? brands.brato;
}

/** Cached active brand id — useful for class-name composition. */
export function getBrandId(): BrandId {
  const raw = (import.meta.env.VITE_BRAND as string | undefined) ?? '';
  const id = raw.trim().toLowerCase() as BrandId;
  return brands[id] ? id : 'brato';
}
